import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { KBOPlayerDailyStatsDto, KBONewsArticleDto, KBOPlayerInfoDto } from '@playball/common/dto/KBO';
import AdComponent from './ads/AdComponent';
import { DataListResponse, DataResponse } from '@playball/common/dto/Common';
import CommonUtil from '../util/CommonUtil';

// stats JSON 문자열 내부의 스탯 타입
interface Stats {
  [key: string]: number | string;
}

// 컴포넌트가 받을 Props 타입
interface PlayerProfileProps {
  playerData: KBOPlayerInfoDto | null;
  playerNewsData: KBONewsArticleDto[];
}


// --- 스탯 이름 매핑 (공통 사용) ---
const STAT_NAME_MAP: { [key: string]: string } = {
  // 타자 스탯
  sR: '득점', sER: '실점', sH: '안타', s2B: '2루타', s3B: '3루타', sHR: '홈런', sRBI: '타점',
  sBB: '볼넷', sSO: '삼진', sSB: '도루', sCS: '도루 실패', sGDP: '병살타',
  sSH: '희생 번트', sSF: '희생 플라이', sIP3: '아웃카운트', sWIN: '승리', sLOSE: '패배', sHOLD: '홀드', sSAVE: '세이브', 

  // 작년 스탯
  //타자 기록:
  pcode: '선수 고유 번호', gyear: '시즌', hra: '타율', gamenum: '게임 수', ab: '타수', run: '득점', 
  hit: '안타', h2: '2루타', h3: '3루타', hr: '홈런', tb: '총 루타', rbi: '타점', sb: '도루', cs: '도루 실패', 
  sh: '희생 번트', sf: '희생 플라이', bb: '볼넷', hp: '사구', kk: '삼진', gd: '병살타', err: '실책', wpa: '승리 확률 기여도 (WPA)',
  war: '대체 수준 대비 승리 기여도 (WAR)', isop: '순장타율 IsoP', babip: '인플레이 타구의 (피)안타율 (BABIP)', obp: '출루율 (OBP)', 
  slg: '장타율 (SLG)', wrcPlus: '조정 득점 창출력 (wRC+)', woba: '가중 출루율 (wOBA)', ops: '(피)출루율 + (피)장타율 (OPS)',
  
  //투수기록:
  era: '평균 자책점', cg: '완투', sho: '완봉', w: '승', l: '패', sv: '세이브', hold: '홀드', bf: '상대한 타자 수', 
  inn: '이닝 수', r: '실점', er: '자책점', innk: '9 이닝 당 삼진 비율', qs: '퀄리티 스타트', whip: '이닝당 볼넷 + 안타 허용률 (WHIP)', 
  innb: '9이닝 당 볼넷 비율', kkBbRt: '볼넷 당 삼진 비율', paBbRt: '볼넷 비율', paKkRt: '삼진 비율',

  // 여기에 필요한 모든 스탯 약어를 추가하세요.
};


const KEYS_TO_FILTER_OUT = ['pcode', 'paFlag', 'tid', 'score', 'inn2', 'inng', 'wra', 'innFlag', 'k9', 'bb9', 'kbb', 'kp', 'bbp', 'team', 'gameCount'];

const POSITION_MAP: {[key: string]: string} = {
  'P': '투수', '1B': '1루수','2B': '2루수',
  '3B': '3루수','SS': '유격수',
  'C': '포수','CF': '중견수','DH': '지명타자',
  'LF': '좌익수','RF': '우익수',
}


// --- 1. 작년 기록 및 단일 스탯 묶음용 테이블 (재사용) ---
interface StatsTableProps {
  score: number;
  average: number;
  gameCount: number;
  json: string;
  title: string;
}

const StatsTable: React.FC<StatsTableProps> = ({ score, average, gameCount, json, title }) => {
  const processedStats = useMemo(() => {
    const statsData = JSON.parse(json);
    const result = [
      {
        name: '총 점수',
        value: String(score),
      },
      {
        name: '게임 수',
        value: String(gameCount),
      },
      {
        name: '평균 점수',
        value: String(average.toFixed(2)),
      }
    ];
    Object.entries(statsData)
      .filter(([key, value]) => !KEYS_TO_FILTER_OUT.includes(key))
      .map(([key, value]) => ({
        name: STAT_NAME_MAP[key] || key,
        value: String(value),
      }))
      .forEach(stats => result.push(stats));
    return result;
  }, [score, gameCount, average, json]);


  return (
    <div style={{ marginBottom: '20px' }}>
      <h4>{title}</h4>
      <div style={{ border: '1px solid #ddd' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {processedStats.map((stat, index) => (
              <>
                { index === 3 && <AdComponent adSlot='list' /> }
                <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '6px 8px' }}>{stat.name}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 500 }}>{stat.value}</td>
                </tr>
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// --- 2. 시즌 경기별 기록 테이블 (재사용) ---
interface SeasonGamesTableProps {
  myTeam: string;
  games: KBOPlayerDailyStatsDto[];
  isPitcher: Boolean;
}

const SeasonGamesTable: React.FC<SeasonGamesTableProps> = ({ myTeam, games, isPitcher }) => {
  // 표시할 주요 스탯 컬럼 선택
  const hitterColumns = ['sH', 's2B', 's3B', 'sHR', 'sR', 'sRBI', 'sBB', 'sSO'];
  const pitcherColumns = ['sIP3', 'sER', 'sH',  'sHR', 'sBB', 'sSO', 'dec'];
  let columns : string[] = []

  if (isPitcher){
    columns = pitcherColumns
    STAT_NAME_MAP['sH'] = '피안타'
    STAT_NAME_MAP['dec'] = 'DEC'
  }
  else{
    columns = hitterColumns
  }
  

  const processedGames = useMemo(() => {
    return games.map(game => {
      try {
        const parsedStats: Stats = JSON.parse(game.json);
        let dec = ''
        if (parsedStats['sWIN'] === 1) dec = 'W'
        else if (parsedStats['sLOSE'] === 1) dec = 'L'
        else if (parsedStats['sHOLD'] === 1) dec = 'H'
        else if (parsedStats['sSAVE'] === 1) dec = 'S'
        parsedStats['dec'] = dec
        return { ...game, parsedStats };
      } catch (e) {
        return { ...game, parsedStats: {} };
      }
    });
  }, [games]);

  return (
    <div>
      <h4>이번 시즌 경기별 기록</h4>
      <div style={{ border: '1px solid #ddd' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center' }}>
          <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f2f2f2' }}>
            <tr>
              <th style={{ padding: '8px' }}>날짜</th>
              <th style={{ padding: '8px' }}>상대팀</th>
              {columns.map(key => <th key={key}>{STAT_NAME_MAP[key] || key}</th>)}
              <th style={{ padding: '8px' }}>점수</th>
            </tr>
          </thead>
          <tbody>
            {processedGames.map(game => (
              <tr key={game.game_date} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '6px 8px' }}>{game.game_date}</td>
                <td style={{ padding: '6px 8px' }}>{game.home_team !== myTeam ? game.home_team : game.away_team}</td>
                {columns.map(key => <td key={key}>{game.parsedStats[key] ?? '-'}</td>)}
                <td style={{ padding: '6px 8px' }}>{game.score ?? 'N/A'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// --- 2. 시즌 경기별 기록 테이블 (재사용) ---
interface NewsProp {
  news: KBONewsArticleDto[];
  playerId: number;
  playerName: string;
}

const News: React.FC<NewsProp> = ({ news, playerId, playerName }) => {

  return (
    <div className="mt-4">
      <h4>최신 뉴스</h4>
      {(!news || news.length === 0) ? (
        <>
          <div className="alert alert-secondary mt-3" role="alert">
            관련된 최신 뉴스가 없습니다.
          </div>
          <AdComponent />
        </>
      ) : (
        <div className="list-group list-group-flush mt-3">
          {news.map((article, index) => (
            <>
              { (index !== 0 && index % 7 === 0) && <AdComponent adSlot="list" /> }
              <a
                href={article.link}
                target="_blank"
                rel="noopener noreferrer"
                key={article.id}
                className="list-group-item list-group-item-action"
                style={{ padding: '1rem 0.5rem' }}
                onClick={(e) => CommonUtil.logEvent('click_news', {
                  player_id: playerId,
                  player_name: playerName,
                  article_id: article.id,
                  article_title: article.title,
                  article_link: article.link,
                })}
              >
                <h6 className="mb-1" dangerouslySetInnerHTML={{ __html: article.title }}></h6>
                <small className="text-muted">
                  {new Date(article.published_at).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
                </small>
              </a>
            </>
          ))}
          {news.length % 7 !== 0 && <AdComponent adSlot="list" />}
        </div>
      )}
    </div>
  );
};


// --- 3. 선수 프로필 메인 컴포넌트 ---
const PlayerProfile: React.FC<PlayerProfileProps> = ({ playerData, playerNewsData }) => {
  if (!playerData?.info) return <p>선수 데이터가 없습니다.</p>;
  
  const { info, yearStatsList, seasonDailyStatsList } = playerData;
  const isPitcher = info.position === 'P'
  const default_image = '/default-image.png'

  return (
    <div className='m-3 mx-auto' style={{ fontFamily: 'sans-serif', maxWidth: '800px' }}>
      <div style={{ 
        padding: '16px', 
        backgroundColor: '#00468E', 
        color: 'white', 
        borderRadius: '8px', 
        marginBottom: '20px' 
      }}>

        <img
          src={info.profile_image || default_image}
          alt={`${info.name} 선수 사진`}
          style={{
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            objectFit: 'cover',
            marginBottom: '15px'
          }}
        />
      
        <h2 style={{ margin: 0 }}>{info.name}</h2>
        <p style={{ margin: '4px 0 0' }}>포지션: {POSITION_MAP[info.position]}</p>
        <p style={{ margin: '4px 0 0' }}>팀: {info.team}</p>
      </div>
      
      {
        yearStatsList.length > 0 
        ? yearStatsList.map(yearStats => <StatsTable score={yearStats.score} average={yearStats.average} gameCount={yearStats.game_count} json={yearStats.json} title={`${yearStats.year}년 연간 기록`} />)
        : <div className="container mt-5 alert alert-warning">선수의 연간 기록이 존재하지 않습니다</div>
      }

      {
        seasonDailyStatsList.length > 0 
        ? <SeasonGamesTable myTeam={info.team} games={seasonDailyStatsList} isPitcher={isPitcher}/>
        : <div className="container mt-5 alert alert-success">선수의 2025년 시즌 기록이 존재하지 않습니다</div>
      }

      {
        playerNewsData && <News news={playerNewsData} playerId={info.id} playerName={info.name}/>
      }
    </div>
  );
};






const PlayerDetailPage: React.FC = () => {
    const { playerId } = useParams<{ playerId: string }>();
    const { fetchApi } = useAuth();

    const [playerNewsData, setPlayerNewsData] = useState<KBONewsArticleDto[]>([]);

    const [playerData, setPlayerData] = useState<KBOPlayerInfoDto | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    const fetchPlayerDetails = useCallback(async () => {
        if (!playerId) {
            setError("선수 ID가 유효하지 않습니다.");
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const data = await fetchApi(`/api/player/${playerId}`) as DataResponse<KBOPlayerInfoDto>;
            setPlayerData(data.data);
        } catch (err: any) {
            setError("선수 정보를 불러오는 데 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    }, [playerId, fetchApi]);


    const fetchPlayerNews = useCallback(async () => {
        if (!playerId) {
            setError("선수 ID가 유효하지 않습니다.");
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const data = await fetchApi(`/api/player/${playerId}/news`) as DataListResponse<KBONewsArticleDto>;
            setPlayerNewsData(data.dataList);
        } catch (err: any) {
            setError("선수 정보를 불러오는 데 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    }, [playerId, fetchApi]);

    useEffect(() => {
        fetchPlayerDetails();
    }, [fetchPlayerDetails]);

    useEffect(() => {
        fetchPlayerNews();
    }, [fetchPlayerNews]);

    /*
    useEffect(() => {
        fetchPlayerNews();
    }, [fetchPlayerNews]);
    */

    if (isLoading) {
        return <div className="container mt-5"><h2>선수 정보 로딩 중...</h2></div>;
    }

    if (error) {
        return <div className="container mt-5 alert alert-danger">{error}</div>;
    }

    if (!playerData) {
        return <div className="container mt-5"><h2>선수 정보를 찾을 수 없습니다.</h2></div>;
    }
    

   return <PlayerProfile playerData={playerData} playerNewsData={playerNewsData} />;
};

export default PlayerDetailPage;
